/**
 * Time formatting. The system stores ISO-8601 UTC throughout (TDD §1.6); the console shows
 * times in UTC by default and lets the viewer switch to their local zone. Everything goes
 * through Intl so no format is hand-rolled.
 */
export type TimeZoneMode = 'utc' | 'local';

const LOCALE = 'en-GB';

function zoneOptions(mode: TimeZoneMode): Intl.DateTimeFormatOptions {
  return mode === 'utc' ? { timeZone: 'UTC' } : {};
}

function zoneSuffix(mode: TimeZoneMode, date: Date): string {
  if (mode === 'utc') return 'UTC';
  const parts = new Intl.DateTimeFormat(LOCALE, { timeZoneName: 'short' }).formatToParts(date);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

export function parseIso(iso: string): Date {
  return new Date(iso);
}

/** "10:45" */
export function formatClock(iso: string, mode: TimeZoneMode, withSeconds = false): string {
  const date = parseIso(iso);
  return new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: false,
    ...zoneOptions(mode),
  }).format(date);
}

/** "10:45 UTC" */
export function formatClockZone(iso: string, mode: TimeZoneMode, withSeconds = false): string {
  return `${formatClock(iso, mode, withSeconds)} ${zoneSuffix(mode, parseIso(iso))}`;
}

/** "17 Sep 2026, 10:45 UTC" */
export function formatDateTime(iso: string, mode: TimeZoneMode, withSeconds = false): string {
  const date = parseIso(iso);
  const day = new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...zoneOptions(mode),
  }).format(date);
  return `${day}, ${formatClockZone(iso, mode, withSeconds)}`;
}

/** "17 Sep 2026" */
export function formatDate(iso: string, mode: TimeZoneMode): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...zoneOptions(mode),
  }).format(parseIso(iso));
}

/** Same calendar day in the chosen zone? Used to shorten timestamps in dense tables. */
export function sameDay(aIso: string, bIso: string, mode: TimeZoneMode): boolean {
  return formatDate(aIso, mode) === formatDate(bIso, mode);
}

/** "3 min ago", "2 h ago", "yesterday" — relative to `now`. */
export function formatRelative(iso: string, nowMs: number): string {
  const diffMs = parseIso(iso).getTime() - nowMs;
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < minute) return diffMs <= 0 ? 'just now' : 'in under a minute';
  if (abs < hour) return rtf.format(Math.round(diffMs / minute), 'minute');
  if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  return rtf.format(Math.round(diffMs / day), 'day');
}

/** "1 h 12 min", "45 min", "3 d 2 h" — for how long a problem has lasted. */
export function formatDuration(ms: number): string {
  if (ms < 0) ms = 0;
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 1) return 'under a minute';
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  return `${minutes} min`;
}

/** "1.1 s", "300 ms", "5 min 0 s" for check durations. */
export function formatMillis(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes} min ${seconds} s`;
}

export function minutesBetween(aIso: string, bIso: string): number {
  return Math.abs(parseIso(aIso).getTime() - parseIso(bIso).getTime()) / 60_000;
}

export function addMinutes(iso: string, minutes: number): string {
  return new Date(parseIso(iso).getTime() + minutes * 60_000).toISOString();
}
